import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileText, ExternalLink, FolderDown } from "lucide-react";
import { StatusBadge, fmtDate } from "@/components/common";
import AttachmentsCell from "@/components/AttachmentsCell";
import { listResource, fileUrl, API } from "@/lib/api";

const isImage = (a) => (a.content_type || "").startsWith("image/") || a.kind === "photo";

const Detail = ({ label, value }) => (
  <div className="border-b border-gray-100 py-2">
    <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">{label}</div>
    <div className="text-sm text-gray-900 mt-0.5">{value || <span className="text-gray-300">—</span>}</div>
  </div>
);

export default function UnitFolder() {
  const { id } = useParams();
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: () => listResource("units"), refetchInterval: 5000 });
  const unit = units.find((u) => u.id === id);

  if (units.length && !unit) {
    return (
      <div data-testid="unit-folder-notfound" className="text-center py-20">
        <p className="text-gray-500">Unit not found.</p>
        <Link to="/units" className="text-[#16265F] font-bold underline">Back to Units</Link>
      </div>
    );
  }
  if (!unit) return <div className="py-20 text-center text-gray-400">Loading…</div>;

  const title = [unit.year, unit.make, unit.model].filter(Boolean).join(" ") || "Unit";
  const photos = (unit.attachments || []).filter(isImage);
  const docs = (unit.attachments || []).filter((a) => !isImage(a));

  return (
    <div data-testid="unit-folder">
      <Link to="/units" data-testid="unit-folder-back" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-500 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> All Units
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 border-b-4 border-gray-900 pb-4 mb-6">
        <div>
          <div className="font-mono-data text-[#946a00] font-bold tracking-widest">{unit.stock_number || "NO STOCK #"}</div>
          <h1 className="font-head text-4xl sm:text-5xl font-bold uppercase tracking-tight text-gray-900">{title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={unit.status} />
          <a
            data-testid="unit-download-folder"
            href={`${API}/units/${unit.id}/folder?gallery=${encodeURIComponent(`${API}/gallery/units/${unit.id}`)}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#16265F] text-white text-sm font-bold uppercase tracking-wide hover:bg-[#0f1c47] transition-colors"
          >
            <FolderDown className="w-4 h-4" /> Download Folder
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: intake / invoice details */}
        <div className="bg-white border-2 border-gray-200 p-5">
          <h2 className="font-head text-lg font-bold uppercase tracking-tight mb-2">Invoice & Intake</h2>
          <Detail label="VIN / Serial #" value={unit.vin} />
          <Detail label="Usage" value={unit.usage} />
          <Detail label="Condition" value={<span className="capitalize">{unit.condition}</span>} />
          <Detail label={unit.condition === "new" ? "Make" : "Previous Owner"} value={unit.condition === "new" ? unit.make : unit.vendor} />
          <Detail label="Date Received" value={unit.date_received ? fmtDate(unit.date_received) : ""} />
          <Detail label="Cost" value={unit.cost} />
          <Detail label="Invoice #" value={unit.invoice_number} />
          <Detail label="List Price" value={unit.sale_price_target} />
          <Detail label="Entered By" value={<span className="font-mono-data uppercase">{unit.initials}</span>} />
          {unit.notes && <Detail label="Description" value={unit.notes} />}
        </div>

        {/* Middle: photos + files */}
        <div className="lg:col-span-2 bg-white border-2 border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head text-lg font-bold uppercase tracking-tight">Photos & Files</h2>
            <AttachmentsCell row={unit} path="units" label="Photos" />
          </div>

          {photos.length === 0 && docs.length === 0 ? (
            <div className="text-gray-400 text-sm py-10 text-center border-2 border-dashed border-gray-200">
              No photos yet — use the Photos button to add intake pictures.
            </div>
          ) : (
            <>
              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-testid="unit-photo-grid">
                  {photos.map((a) => (
                    <a key={a.id} href={fileUrl(a.path)} target="_blank" rel="noreferrer" className="block group">
                      <img src={fileUrl(a.path)} alt={a.filename} className="w-full h-32 object-cover border-2 border-gray-200 group-hover:border-[#16265F] transition-colors" />
                    </a>
                  ))}
                </div>
              )}
              {docs.length > 0 && (
                <ul className="space-y-1">
                  {docs.map((a) => (
                    <li key={a.id}>
                      <a href={fileUrl(a.path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#16265F] hover:underline">
                        <FileText className="w-4 h-4" /> {a.filename} <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
